<div class="itcm-container">

  <!-- ═══════════════════════════════════════════════════════════════════════
       STEP 1 – Source & Document selection
       ═══════════════════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="currentStep === 1">

    <!-- Header -->
    <div class="itcm-header">
      <h2 class="itcm-title">How would you like to import test cases?</h2>
      <button
        type="button"
        class="accello-icon-btn"
        (mousedown)="isActive = true"
        (mouseup)="isActive = false"
        (mouseleave)="isActive = false"
        (click)="onCancel()"
        aria-label="Close">
        <img
          [src]="isActive ? 'assets/images/virtusa-icons/close-dark.svg' : 'assets/images/virtusa-icons/close.svg'"
          alt="Close"/>
      </button>
    </div>

    <!-- Body -->
    <div class="itcm-body">

      <!-- Recommended actions banner -->
      <div class="itcm-banner">
        <img class="itcm-banner-icon" src="assets/images/virtusa-icons/recomendation.svg"
          alt="" aria-hidden="true" width="28" height="28"/>
        <div class="itcm-banner-content">
          <span class="itcm-banner-title">Recommended Actions</span>
          <p class="itcm-banner-text">
            Manage all source files in
            <strong>Project Settings &rarr; Connections</strong>.
            Upload or configure your files there to make them available here.
          </p>
        </div>
      </div>

      <!-- Import source cards -->
      <div class="itcm-section">
        <p class="itcm-label">Import Test Cases From <span class="itcm-required">*</span></p>
        <fieldset class="itcm-source-cards">
          <label
            *ngFor="let src of importSources"
            class="itcm-source-card"
            [class.itcm-source-card--selected]="selectedSource === src.key && !src.disabled"
            [class.itcm-source-card--disabled]="src.disabled">
            <input type="radio" name="importSource" [value]="src.key"
              [disabled]="src.disabled" [(ngModel)]="selectedSource" class="itcm-source-radio"/>
            <span class="itcm-source-card-title">{{ src.title }}</span>
            <p class="itcm-source-card-desc">{{ src.description }}</p>
          </label>
        </fieldset>
      </div>

      <!-- ALM connection (Zephyr only) -->
      <div class="itcm-fields-row" *ngIf="isZephyrSource">
        <div class="itcm-field">
          <label class="itcm-label" for="itcm-alm-provider">
            Connection Provider <span class="itcm-required">*</span>
          </label>
          <div class="itcm-select-wrapper">
            <select
              id="itcm-alm-provider"
              class="itcm-select"
              [(ngModel)]="selectedAlmProvider"
              (ngModelChange)="onAlmProviderChange()"
              [disabled]="isLoadingConnections">
              <option value="" disabled hidden>
                {{ isLoadingConnections ? 'Loading…' : 'Select provider' }}
              </option>
              <option *ngFor="let provider of almProviders" [value]="provider">{{ provider }}</option>
            </select>
            <img
              class="itcm-select-arrow"
              *ngIf="!isLoadingConnections"
              src="assets/images/virtusa-icons/drop-arrow.svg"
              alt=""
              aria-hidden="true"
              width="12"
              height="8"
            />
            <span class="itcm-spinner" *ngIf="isLoadingConnections"></span>
          </div>
        </div>

        <div class="itcm-field">
          <label class="itcm-label" for="itcm-alm-name">
            Connection Name <span class="itcm-required">*</span>
          </label>
          <div class="itcm-select-wrapper">
            <select
              id="itcm-alm-name"
              class="itcm-select"
              [(ngModel)]="selectedAlmConnectionId"
              [disabled]="isLoadingConnections || !selectedAlmProvider">
              <option value="" disabled hidden>
                {{ isLoadingConnections ? 'Loading…' : 'Select connection name' }}
              </option>
              <option
                *ngFor="let conn of almConnectionsForProvider"
                [value]="connectionRecordId(conn)">
                {{ conn.connectionName }}
              </option>
            </select>
            <img
              class="itcm-select-arrow"
              *ngIf="!isLoadingConnections"
              src="assets/images/virtusa-icons/drop-arrow.svg"
              alt=""
              aria-hidden="true"
              width="12"
              height="8"
            />
            <span class="itcm-spinner" *ngIf="isLoadingConnections"></span>
          </div>
          <p class="itcm-field-error" *ngIf="connectionsError">
            Failed to load connections. Please try again.
          </p>
          <p
            class="itcm-field-empty"
            *ngIf="!isLoadingConnections && !connectionsError && almConnections.length === 0">
            No ALM connections found. Add one in Project Settings → Connections.
          </p>
        </div>
      </div>

      <!-- Document Type & Name (file import only) -->
      <div class="itcm-fields-row" *ngIf="!isZephyrSource && !showAddDocForm">
        <div class="itcm-field">
          <label class="itcm-label" for="itcm-doc-type">
            Document Type <span class="itcm-required">*</span>
          </label>
          <div class="itcm-select-wrapper">
            <select id="itcm-doc-type" class="itcm-select"
              [(ngModel)]="selectedDocumentType" (ngModelChange)="onDocumentTypeChange()">
              <option *ngFor="let dt of documentTypes" [value]="dt.value">{{ dt.label }}</option>
            </select>
            <img
              class="itcm-select-arrow"
              src="assets/images/virtusa-icons/drop-arrow.svg"
              alt=""
              aria-hidden="true"
              width="12"
              height="8"
            />
          </div>
        </div>

        <div class="itcm-field">
          <label class="itcm-label" for="itcm-doc-name">
            Document Name <span class="itcm-required">*</span>
          </label>
          <div class="itcm-select-wrapper">
            <select id="itcm-doc-name" class="itcm-select"
              [(ngModel)]="selectedConnectionId" [disabled]="isLoadingConnections">
              <option value="" disabled hidden>
                {{ isLoadingConnections ? 'Loading…' : 'Select document name' }}
              </option>
              <option *ngFor="let conn of filteredConnections" [value]="conn._id ?? conn.id">
                {{ conn.connectionName }}
              </option>
            </select>
            <img
              class="itcm-select-arrow"
              *ngIf="!isLoadingConnections"
              src="assets/images/virtusa-icons/drop-arrow.svg"
              alt=""
              aria-hidden="true"
              width="12"
              height="8"
            />
            <span class="itcm-spinner" *ngIf="isLoadingConnections"></span>
          </div>
          <p class="itcm-field-error" *ngIf="connectionsError">
            Failed to load connections. Please try again.
          </p>
          <p class="itcm-field-empty"
            *ngIf="!isLoadingConnections && !connectionsError && filteredConnections.length === 0">
            No {{ selectedDocumentType.toUpperCase() }} connections found.
          </p>
        </div>
      </div>

      <!-- Add document link -->
      <button type="button" class="itcm-add-link" *ngIf="!isZephyrSource && !showAddDocForm" (click)="onAddDocument()">
        + Add a new document
      </button>

      <!-- Inline add-document form -->
      <div class="itcm-add-doc-form" *ngIf="!isZephyrSource && showAddDocForm">
        <div class="itcm-add-doc-type-row">
          <label class="itcm-label" for="itcm-add-doc-type">
            Document Type <span class="itcm-required">*</span>
          </label>
          <div class="itcm-add-doc-actions">
            <button type="button" class="itcm-add-doc-action itcm-add-doc-action--confirm"
              [disabled]="!addDocFile || isUploadingDoc" (click)="onConfirmAddDocument()"
              aria-label="Save document">
              <svg *ngIf="!isUploadingDoc" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8l4 4 6-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span *ngIf="isUploadingDoc" class="itcm-spinner itcm-spinner--sm"></span>
            </button>
            <button type="button" class="itcm-add-doc-action itcm-add-doc-action--cancel"
              [disabled]="isUploadingDoc" (click)="onCancelAddDocument()" aria-label="Cancel">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="itcm-select-wrapper">
          <select id="itcm-add-doc-type" class="itcm-select"
            [(ngModel)]="addDocType" (ngModelChange)="onAddDocTypeChange()">
            <option *ngFor="let dt of documentTypes" [value]="dt.value">{{ dt.label }}</option>
          </select>
          <img
            class="itcm-select-arrow"
            src="assets/images/virtusa-icons/drop-arrow.svg"
            alt=""
            aria-hidden="true"
            width="12"
            height="8"
          />
        </div>
        <p class="itcm-label itcm-add-doc-file-label">
          Document <span class="itcm-required">*</span>
        </p>
        <ng-container *ngFor="let k of [fileUploadKey]">
          <app-file-upload [data]="addDocUploadConfig" (filesSelected)="onFilesSelected($event)">
          </app-file-upload>
        </ng-container>
        <p class="itcm-field-error" *ngIf="uploadError">{{ uploadError }}</p>
      </div>

      <!-- Preview load error -->
      <p class="itcm-field-error" *ngIf="previewError">{{ previewError }}</p>

    </div>

    <!-- Footer -->
    <div class="itcm-footer">
      <span class="itcm-step-label">Step 1 of {{ isZephyrSource ? 3 : 2 }}</span>
      <div class="itcm-footer-actions">
        <button type="button" class="btn-accello-secondary mx-2" (click)="onCancel()">Cancel</button>
        <button type="button" class="btn-accello-primary-light d-inline-flex align-items-center gap-2"
          [disabled]="!canContinue || isLoadingPreview"
          (click)="onContinue()">
          <span *ngIf="isLoadingPreview" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          {{ isLoadingPreview ? 'Extracting...' : 'Continue' }}
        </button>
      </div>
    </div>

  </ng-container>

  <!-- ═══════════════════════════════════════════════════════════════════════
       STEP 2 – Field Mapping
       ═══════════════════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="currentStep === 2">
    <div class="itcm-container--step2 d-flex flex-column overflow-hidden">

      <!-- Header -->
      <div class="itcm-header">
        <h2 class="itcm-title">Field Mapping</h2>
        <button
          type="button"
          class="accello-icon-btn"
          (mousedown)="isActive = true"
          (mouseup)="isActive = false"
          (mouseleave)="isActive = false"
          (click)="onCancel()"
          aria-label="Close">
          <img
            [src]="isActive ? 'assets/images/virtusa-icons/close-dark.svg' : 'assets/images/virtusa-icons/close.svg'"
            alt="Close"/>
        </button>
      </div>

      <!-- Scrollable mapping table -->
      <div class="itcm-mapping-body">
        <app-field-mapping
          [fields]="systemFields"
          [columns]="extractedColumnNames"
          [sourceLabel]="selectedDocumentType.toUpperCase() + ' Field'"
          idPrefix="itcm-map"
          [mappings]="fieldMappings">
        </app-field-mapping>
      </div>

      <!-- Import error -->
      <p class="itcm-import-error" *ngIf="importError">{{ importError }}</p>

      <!-- Footer -->
      <div class="itcm-footer">
        <span class="itcm-step-label">Step 2 of 2</span>
        <div class="itcm-footer-actions">
          <button type="button" class="btn-accello-secondary mx-2" [disabled]="isImporting" (click)="onCancel()">
            Cancel
          </button>
          <button type="button" class="btn-accello-primary-light d-inline-flex align-items-center gap-2"
            [disabled]="!requiredFieldsMapped || isImporting"
            (click)="onImport()">
            <span *ngIf="isImporting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            {{ isImporting ? 'Importing...' : 'Import' }}
          </button>
        </div>
      </div>

    </div>
  </ng-container>

</div>
